import { BookOpen, Trash } from "lucide-react"
import { useCallback, useId } from "react"
import { useTranslation } from "react-i18next"

import {
  ClassResultCard,
  ClassResultContent,
  ClassResultDetails,
  ClassResultHeader,
  ClassResultSpecialities,
  ClassSearchResults,
} from "@/components/search/classes/class-result"
import { ClassSearchProvider } from "@/components/search/classes/class-search-provider"
import { ClassSearchFacets } from "@/components/search/classes/class-search-sidebar"
import { FullTextFilter } from "@/components/search/full-text-filter"
import { ShortlistedSearchWorkspace } from "@/components/search/shortlisted-search-workspace"
import { SearchDetailLink } from "@/components/search/search-return-navigation"
import { Sidebar } from "@/components/search/search-layout"
import {
  ConfirmationDialog,
  ConfirmationDialogTrigger,
} from "@/components/shared/confirmation-dialog"
import { Button } from "@/components/ui/button"
import { buttonVariants } from "@/components/ui/button-variants"
import { CardFooter } from "@/components/ui/card"
import { SearchUrlState } from "@/lib/redux-search/search-url-state"

import { type IndexProps } from "./page-props"
import { Link, useProps } from "@/lib/page-rendering"

type ClassSearchHit = {
  id: string
  class_details: {
    name: string
    curriculum_key?: string
    subject_key?: string
    class_size_range?: { gte?: number; lte?: number }
    speciality_attributes: string[]
  }
  profile_details?: { name?: string }
}

const classNameFields = [
  "class_details.name^3",
  "class_details.name.autocomplete^2",
  "class_details.name.keyword^5",
  "profile_details.name^3",
  "profile_details.name.autocomplete^2",
  "profile_details.name.keyword^5",
  "keywords",
  "keywords.keyword",
]

// The shortlist is read from this component's own props and threaded down
// rather than pulled from `usePage`, because inside the preference sheet this
// page renders as an embedded page and `usePage` returns the host's props.
export default function Page() {
  const { shortlist } = useProps<IndexProps>()
  const ResultComponent = useCallback(
    ({ result }: { result: ClassSearchHit }) => (
      <Result result={result} shortlistId={shortlist.id} />
    ),
    [shortlist.id],
  )

  return (
    <ClassSearchProvider
      url={`/search/shortlists/${shortlist.id}/classes/query`}
    >
      <SearchUrlState />
      <ShortlistedSearchWorkspace
        activeTab="classes"
        results={<ClassSearchResults ResultComponent={ResultComponent} />}
        shortlist={shortlist}
        sidebar={
          <Sidebar>
            <ClassSearchFacets />
          </Sidebar>
        }
        topControls={
          <>
            <FullTextFilter fields={classNameFields} filterId="class_name" />
          </>
        }
      />
    </ClassSearchProvider>
  )
}

function Result({
  result,
  shortlistId,
}: {
  result: ClassSearchHit
  shortlistId: string
}) {
  const titleId = useId()
  const { t } = useTranslation(["shortlists", "common"])

  return (
    <ClassResultCard titleId={titleId}>
      <ClassResultHeader result={result} titleId={titleId} />
      <ClassResultContent>
        <ClassResultDetails result={result} />
        <ClassResultSpecialities result={result} />
      </ClassResultContent>
      <CardFooter className="flex flex-row flex-wrap gap-4">
        <Button asChild>
          <SearchDetailLink href={`/search/classes/${result.id}`}>
            <BookOpen />
            {t("shortlists:workspace.classes.view_class")}
          </SearchDetailLink>
        </Button>
        <ConfirmationDialog
          action={
            <Link
              as={Button}
              className={buttonVariants({ variant: "destructive" })}
              href={`/search/shortlists/${shortlistId}/classes/${result.id}`}
              method="delete"
            >
              {t("common:actions.confirm")}
            </Link>
          }
        >
          <ConfirmationDialogTrigger asChild>
            <Button variant="destructive-outline">
              <Trash />
              {t("common:actions.remove")}
            </Button>
          </ConfirmationDialogTrigger>
        </ConfirmationDialog>
      </CardFooter>
    </ClassResultCard>
  )
}
